"use strict";

class SSDateService {

    //===========================================
    // Date Formats
    dateFormat(date, format = null) {
        if (!date) {
            return;
        }

        let utcDate = moment.utc(date);

        if (utcDate._isValid) {
            let localDate = utcDate.local();
            if (format) {
                return localDate.format(format);
            } else {
                return localDate.toLocaleString();
            }
        }
    }

    dateFormatDefault(date) {
        return this.dateFormat(date);
    }

    dateFormat3(date) {
        return this.dateFormat(date, 'DD/MM/YYYY');
    }

    dateFormat4(date) {
        return this.dateFormat(date, 'YYYY-MM-DD');
    }

    dateFormat5(date) {
        return this.dateFormat(date, 'MMMM DD, YYYY');
    }

    //-------------------------------------------

    getFormatOptionForYear() {
        return {
            formatYear: 'yyyy',
            startingDay: 1,
            minMode: 'year'
        }
    }

    getFormatOptionForMonth() {
        return {
            formatYear: 'yyyy-MM',
            minMode: 'month'
        }
    }

    /**
     * Creates date object with local time from UTC date string.
     *
     * @param utcDateString format "YYYY-MM-DD hh:mm:ss"
     * @param defaultVal
     */
    toLocalDate(utcDateString, defaultVal) {
        if (!utcDateString) {
            return defaultVal;
        }
        utcDateString = utcDateString.trim();

        var exp = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})$/;
        if (!exp.test(utcDateString)) {
            return defaultVal;
        }

        var dateArray = utcDateString.split(' ');

        var dateString = dateArray[0];
        var timeString = dateArray[1];

        dateArray = dateString.split('-');
        var timeArray = timeString.split(':');

        var year = dateArray[0];
        var month = dateArray[1];
        var day = dateArray[2];
        var hours = timeArray[0];
        var minutes = timeArray[1];
        var seconds = timeArray[2];

        return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
    }

    /**
     * Normalise a settings-blob date to a plain `YYYY-MM-DD` calendar date.
     *
     * The canonical stored shape for a `blackout_days[].date` is `YYYY-MM-DD HH:mm:ss` (noon),
     * which is what {@link formateWithTimeAsNoon} writes. Some rows in the wild were written by
     * another client as day-first `DD/MM/YYYY` (e.g. `07/09/2026`); every consumer that compared
     * the raw string simply never matched them, so the blackout silently did not apply at all.
     * Accepting both shapes here stops that being a no-op.
     *
     * @remarks
     * WEB-4312. This is the client mirror of the server's
     * `KeyHandoverAppointmentService::normaliseSettingsDate()` (WEB-4309) and must keep agreeing
     * with it, so the two halves read a stored value the same way.
     *
     * Deliberately NOT implemented as a bare `moment(value)`: moment's default parser reads
     * `07/09/2026` as **month-first** (9 July), the opposite of what the UI means by it — silently
     * wrong by two months, which is worse than the original "no match at all". Each accepted shape
     * is therefore parsed with an explicit format in strict mode, so an impossible date such as
     * `31/02/2026` is rejected rather than rolled over into March. Anything unrecognised returns
     * null and the caller treats the entry as "does not match", rather than guessing at it.
     *
     * @param {*} date - Raw `date` value from a settings blob entry.
     * @returns {string|null} The calendar date as `YYYY-MM-DD`, or null when absent/unparseable.
     */
    parseSettingsDate(date) {
        if (!date || typeof date !== 'string')
            return null;

        let value = date.trim();

        if (!value)
            return null;

        // Canonical: YYYY-MM-DD, with or without a trailing time part.
        let canonical = value.match(/^(\d{4}-\d{2}-\d{2})(?:[ T].*)?$/);

        if (canonical)
            return moment(canonical[1], 'YYYY-MM-DD', true).isValid() ? canonical[1] : null;

        // Legacy/foreign: day-first DD/MM/YYYY, with or without a trailing time part.
        let dayFirst = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T].*)?$/);

        if (dayFirst) {
            let parsed = moment(dayFirst[3] + '-' + dayFirst[2] + '-' + dayFirst[1], 'YYYY-M-D', true);

            return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
        }

        return null;
    }

    /**
     * Render a settings-blob date in the day-first `DD/MM/YYYY` shape the settings pages bind to.
     *
     * @remarks
     * WEB-4312. Replaces `moment(moment(value, 'YYYY-MM-DD HH:mm:ss')).utc().format('DD/MM/YYYY')`,
     * which produced the literal string `"Invalid date"` for any value that was not already
     * canonical. That string then became a `blackout_days` map key (so the entry could never be
     * matched, and two bad entries merged into one bucket) and — because it contains a space —
     * was written straight back into the settings blob on the next save, destroying the intended
     * date beyond recovery. Returning the value **unchanged** when it cannot be parsed keeps the
     * original visible to the user and lets {@link formateWithTimeAsNoon} preserve it verbatim.
     *
     * @param {*} date - Raw `date` value from a settings blob entry.
     * @returns {*} The date as `DD/MM/YYYY`, or the input unchanged when it cannot be parsed.
     */
    formatSettingsDateAsDayFirst(date) {
        const self = this;

        let calendarDate = self.parseSettingsDate(date);

        if (!calendarDate)
            return date;

        return moment(calendarDate, 'YYYY-MM-DD', true).format('DD/MM/YYYY');
    }

    /**
     * Convert a UI date into the `YYYY-MM-DD 12:00:00` shape the API stores.
     *
     * @remarks
     * WEB-4312 tightened this. The old implementation returned its input unchanged whenever the
     * string contained a space — a test meant to pass through an already-formatted
     * `YYYY-MM-DD HH:mm:ss` value, but one that also let the literal `"Invalid date"` through and
     * persisted it. The shape is now tested for properly, and a value that is not a date at all
     * returns null so it can never be written. Note this helper has ~65 callers across sales,
     * payments, lawyer and settings screens, so the recognised shapes must stay permissive:
     * `YYYY-MM-DD`, day-first `DD/MM/YYYY` (single- or double-digit day/month), and either of
     * those carrying a time part.
     *
     * @param {*} date - Date string from a datepicker or an already-formatted stored value.
     * @returns {string|null|undefined} `YYYY-MM-DD 12:00:00`, the input unchanged when it already
     *          carries a time, undefined when the input is empty, or null when it is not a date.
     */
    formateWithTimeAsNoon(date) {
        const self = this;

        if (!date)
            return;

        if (typeof date !== 'string')
            return date;

        // Already a formatted date-time — pass it straight through, unchanged and idempotent.
        if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(date.trim()))
            return date;

        let calendarDate = self.parseSettingsDate(date);

        // WEB-4312: not a date at all (e.g. "Invalid date") — refuse to persist it.
        if (!calendarDate)
            return null;

        return calendarDate + ' 12:00:00';
    }

    getLocalTimezone(){
        let timezone = "Asia/Singapore";

        try{
            timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        }catch (e) {
            console.warn("Unable to get local timezone. Default timezone will be used.", e);
        }
        return timezone;
    }
}

SSDateService.$inject = [];
app.service('SSDateService', SSDateService);